// src/modules/app-views/omnichannel/pages/Init/modals/OmnichannelParametersModal/modals/AddWhatsappModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useMemo, useEffect, useRef } from 'react';
import { WhatsAppOutlined } from '@ant-design/icons';
import GenericModal from 'components/core/GenericModal';
import appService from 'services/appService';
import omnichannelService from 'modules/app-views/omnichannel/service';
import step1Fn from './Step1';
import step2Fn from './Step2';
import step3Fn from './Step3';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const AddWhatsappModal = ({ show, setShow, afterClose, editingRecord, onSuccess }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const isEditing = !!editingRecord;
  const lockedSource = editingRecord?.sourceType || null;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  // Refs mirror state so effects always read the latest values (avoids stale closures)
  const instanceIdRef = useRef(null);
  const sourceRef = useRef(null);
  const connectionStatusRef = useRef('disconnected');

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [source, setSource] = useState(lockedSource || null);
  const [instanceId, setInstanceId] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [savedDisplayName, setSavedDisplayName] = useState('');
  const [savedPhoneNumber, setSavedPhoneNumber] = useState('');
  const [disableBack, setDisableBack] = useState(false);
  // Disables step1 fields while the Baileys instance is being created
  const [step1Disabled, setStep1Disabled] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // When modal closes while a Baileys instance was created but QR was never scanned,
  // delete the orphan instance from the database.
  // Using refs to always read latest values regardless of closure timing.
  useEffect(() => {
    if (!show && instanceIdRef.current && sourceRef.current === 'baileys' && connectionStatusRef.current !== 'connected') {
      omnichannelService.removeBaileysInstance(instanceIdRef.current).catch(() => {});
    }
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = () => {
    // Both Baileys and Meta: already saved before reaching step3 — just close
  };

  const handleAfterClose = () => {
    // Cleanup of orphan Baileys instance is handled in the useEffect above (show dep).
    // Here we just reset local state for the next modal open.
    sourceRef.current = null;
    instanceIdRef.current = null;
    connectionStatusRef.current = 'disconnected';
    setSource(lockedSource || null);
    setInstanceId(null);
    setConnectionStatus('disconnected');
    setSavedDisplayName('');
    setSavedPhoneNumber('');
    setDisableBack(false);
    setStep1Disabled(false);
    afterClose?.();
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const initialValues = useMemo(() => {
    if (!editingRecord) return {};
    return {
      displayName: editingRecord.displayName || '',
      phoneNumber: { value: editingRecord.phoneNumber || '' },
      sourceType: editingRecord.sourceType || 'meta',
      phoneNumberId: editingRecord.phoneNumberId || '',
      businessAccountId: editingRecord.businessAccountId || '',
      // accessToken intentionally blank — not returned by API
      accessToken: '',
    };
  }, [editingRecord]);

  const steps = useMemo(
    () => [
      {
        ...step1Fn({ isEditing, lockedSource, disabled: step1Disabled }),
        onEnter: ({ goFocus }) => {
          setDisableBack(false);
          setStep1Disabled(false);
          goFocus('displayName');
        },
        onNext: async ({ form, setLoading }) => {
          const values = form.getFieldsValue(true);
          const currentSource = values.sourceType;
          const raw = values.phoneNumber;
          const phoneValue = typeof raw === 'object' ? raw?.value : raw;

          sourceRef.current = currentSource;
          setSource(currentSource);
          setSavedDisplayName(values.displayName || '');
          setSavedPhoneNumber(phoneValue || '');

          // For Baileys, create the instance now so step2 can show the QR code
          if (currentSource === 'baileys' && !isEditing) {
            try {
              setLoading(true);
              setStep1Disabled(true);

              if (!phoneValue) {
                appService.message({ flag: 'e', text: 'required_field' });
                return false;
              }

              const [data] = await omnichannelService.createBaileysInstance({
                displayName: values.displayName || '',
                phoneNumber: phoneValue,
              });

              if (data?._id) {
                instanceIdRef.current = data._id;
                setInstanceId(data._id);
                const status = data.connectionStatus || 'connecting';
                connectionStatusRef.current = status;
                setConnectionStatus(status);
              } else {
                appService.message({ flag: 'e', text: 'error_saving' });
                return false;
              }
            } catch (error) {
              console.error('[AddWhatsappModal] createBaileysInstance error:', error);
              const msg = error?.response?.data?.message || 'error_saving';
              appService.message({ flag: 'e', text: msg });
              setStep1Disabled(false);
              return false;
            } finally {
              setLoading(false);
            }
          }
        },
      },
      {
        ...step2Fn({
          source,
          instanceId,
          savedPhoneNumber,
          onStatusChange: status => {
            connectionStatusRef.current = status;
            setConnectionStatus(status);
          },
          isEditing,
          // Avatar props — only relevant in edit mode
          profilePicture: isEditing ? (editingRecord?.profilePictureBase64 || null) : null,
          recordId: isEditing ? (editingRecord?._id || null) : null,
          displayName: savedDisplayName,
          onAvatarUpdate: isEditing ? onSuccess : undefined,
        }),
        onEnter: () => setDisableBack(false),
        // Baileys: guard manual Next click — only allowed after QR connection (auto-advance handles the normal flow)
        // Meta: save credentials and advance to step3
        onNext: source === 'baileys'
          ? () => {
              if (connectionStatusRef.current !== 'connected') {
                appService.message({ flag: 'w', text: 'waiting_connection' });
                return false;
              }
            }
          : async ({ form, setLoading }) => {
              try {
                setLoading(true);
                const values = form.getFieldsValue(true);
                const raw = values.phoneNumber;
                const phoneValue = typeof raw === 'object' ? raw?.value : raw;

                const payload = {
                  displayName: values.displayName,
                  phoneNumber: phoneValue || values.phoneNumber,
                  phoneNumberId: values.phoneNumberId,
                  businessAccountId: values.businessAccountId,
                  accessToken: values.accessToken,
                };

                // Strip empty accessToken on edit (means user didn't change it)
                if (isEditing && !payload.accessToken) {
                  delete payload.accessToken;
                }

                if (isEditing) {
                  await omnichannelService.updateWhatsappPhone(editingRecord._id, payload);
                } else {
                  const [saved] = await omnichannelService.addWhatsappPhone(payload);
                  setSavedDisplayName(saved?.displayName || values.displayName || '');
                  setSavedPhoneNumber(saved?.phoneNumber || phoneValue || '');
                }

                appService.message({ flag: 's', text: 'saved_successfully' });
              } catch (error) {
                console.error('[AddWhatsappModal] onNext Meta error:', error);
                const msg = error?.response?.data?.message || 'error_saving';
                appService.message({ flag: 'e', text: msg });
                return false;
              } finally {
                setLoading(false);
              }
            },
      },
      {
        ...step3Fn({
          source,
          connectionStatus,
          displayName: savedDisplayName,
          phoneNumber: savedPhoneNumber,
        }),
        onEnter: () => {
          setDisableBack(true);
          // Trigger parent reload as soon as step3 is shown (save already completed)
          onSuccess?.();
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [source, instanceId, connectionStatus, isEditing, lockedSource, savedDisplayName, savedPhoneNumber, step1Disabled],
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      icon={<WhatsAppOutlined style={{ color: '#25D366' }} />}
      title={isEditing ? 'edit_phone' : 'add_phone'}
      steps={steps}
      onFinish={handleFinish}
      initialValues={initialValues}
      afterClose={handleAfterClose}
      width={520}
      maskClosable={false}
      disableBack={disableBack}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default AddWhatsappModal;
